import React, { useEffect, useRef, useState } from 'react';
import { FiCamera, FiX } from 'react-icons/fi';

const BarcodeCameraScanner = ({
  isOpen,
  onClose,
  onDetected,
  title = 'Escanear código de barras',
}) => {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const activeRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const onDetectedRef = useRef(onDetected);
  const [error, setError] = useState('');
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    onCloseRef.current = onClose;
    onDetectedRef.current = onDetected;
  }, [onClose, onDetected]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;
    activeRef.current = true;
    setError('');
    setIsStarting(true);

    const stopScanner = () => {
      activeRef.current = false;
      if (controlsRef.current) {
        controlsRef.current.stop();
        controlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };

    const startScanner = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Este navegador no permite usar la cámara.');
        }

        const { BarcodeFormat, BrowserMultiFormatReader } = await import('@zxing/browser');
        const barcodeFormats = [
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
          BarcodeFormat.CODE_93,
          BarcodeFormat.CODABAR,
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.ITF,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
          BarcodeFormat.QR_CODE,
        ];
        const codeReader = new BrowserMultiFormatReader();
        codeReader.possibleFormats = barcodeFormats;
        const controls = await codeReader.decodeFromConstraints({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        }, videoRef.current, (result, scanError, controlsFromCallback) => {
          if (!activeRef.current) return;
          const rawValue = result?.getText?.();
          if (rawValue) {
            activeRef.current = false;
            controlsFromCallback.stop();
            controlsRef.current = null;
            onDetectedRef.current(String(rawValue).trim());
            onCloseRef.current();
            return;
          }

          if (scanError && scanError.name !== 'NotFoundException') {
            console.warn('Error scanning barcode frame:', scanError);
          }
        });

        if (cancelled) {
          controls.stop();
          return;
        }

        controlsRef.current = controls;
      } catch (scannerError) {
        stopScanner();
        if (!cancelled) {
          console.error('Error starting barcode scanner:', scannerError);
          const errorName = scannerError?.name || '';
          if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
            setError('Permite el acceso a la cámara para escanear códigos.');
          } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
            setError('No se encontró una cámara disponible.');
          } else {
            setError(scannerError.message || 'No se pudo iniciar la cámara.');
          }
        }
      } finally {
        if (!cancelled) {
          setIsStarting(false);
        }
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      stopScanner();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-6">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            <FiCamera className="shrink-0 text-blue-600" size={18} />
            <h2 className="truncate text-base font-semibold text-gray-900">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Cerrar escáner"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-gray-900">
            <video
              ref={videoRef}
              className="h-full w-full object-cover"
              muted
              playsInline
            />
            <div className="pointer-events-none absolute inset-x-[12%] top-1/2 h-20 -translate-y-1/2 rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]" />
            {isStarting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-medium text-white">
                Iniciando cámara...
              </div>
            )}
          </div>

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              Enfoca el código dentro del marco. Se procesará al detectar una coincidencia.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BarcodeCameraScanner;
